<template>
  <div class="products-page">
    <header class="products-page__header">
      <div>
        <h1 class="products-page__title">{{ $t('catalog.products.title') }}</h1>
        <p class="products-page__subtitle">{{ $t('catalog.products.subtitle') }}</p>
      </div>

      <div class="products-page__actions">
        <button type="button" class="products-page__btn products-page__btn--labels" @click="openLabels">
          <i class="mdi mdi-printer"></i>
          {{ $t('catalog.products.printLabels') }}
          <em v-if="selectedCount">{{ selectedCount }}</em>
        </button>
        <button type="button" class="products-page__btn products-page__btn--primary" @click="openCreate">
          <i class="mdi mdi-plus"></i>
          {{ $t('catalog.products.add') }}
        </button>
        <button type="button" class="products-page__btn products-page__btn--ghost" @click="$router.push('/cash')">
          <i class="mdi mdi-arrow-left"></i>
          {{ $t('catalog.products.backToCash') }}
        </button>
      </div>
    </header>

    <section class="products-page__section">
      <div class="products-page__section-head">
        <h2>{{ $t('catalog.common.categories') }}</h2>
      </div>
      <div class="products-page__chips">
        <button
          type="button"
          class="products-page__chip"
          :class="{ 'products-page__chip--active': !categoryId }"
          @click="selectCategory(null)"
        >
          {{ $t('catalog.common.all') }}
        </button>
        <button
          v-for="category in categories"
          :key="category.id"
          type="button"
          class="products-page__chip"
          :class="{ 'products-page__chip--active': categoryId === category.id }"
          @click="selectCategory(category.id)"
        >
          {{ category.name }}
        </button>
      </div>
    </section>

    <section class="products-page__section">
      <v-text-field
        v-model="search"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('catalog.common.searchByName')"
        class="products-page__search"
      />
    </section>

    <div class="products-page__labels-bar">
      <span>
        {{ selectedCount
          ? $t('catalog.products.selectedForLabels', { count: selectedCount })
          : $t('catalog.products.selectForLabels') }}
      </span>
      <button type="button" @click="openLabels">
        <i class="mdi mdi-printer"></i>
        {{ $t('catalog.products.printLabels') }}
      </button>
    </div>

    <section v-if="products.length" class="products-page__grid">
      <article
        v-for="product in products"
        :key="product.id"
        class="product-card"
        :class="{ 'product-card--settled': product.settlement_done }"
      >
        <button
          type="button"
          class="product-card__delete"
          :title="$t('catalog.common.delete')"
          @click="askDelete(product)"
        >
          <i class="mdi mdi-delete-outline"></i>
        </button>

        <div v-if="product.image_thumb_url || product.image_url" class="product-card__image">
          <img :src="product.image_thumb_url || product.image_url" :alt="product.name">
        </div>
        <div class="product-card__top">
          <label class="product-card__select">
            <input :checked="isSelected(product.id)" type="checkbox" @change="toggleProductSelection(product)">
            <span>{{ $t('catalog.products.selectForLabels') }}</span>
          </label>
          <h3 class="product-card__name">{{ product.name }}</h3>
          <span class="product-card__unit">{{ getCountTypeLabel(product.count_type) }}</span>
        </div>

        <div
          v-if="product.settlement_done || settlementProgress(product) > 0"
          class="product-card__badge"
          :class="product.settlement_done ? 'product-card__badge--done' : 'product-card__badge--partial'"
        >
          <i :class="product.settlement_done ? 'mdi mdi-check-circle' : 'mdi mdi-progress-check'"></i>
          {{ product.settlement_done
            ? $t('catalog.products.settlementDone')
            : $t('catalog.products.settlementPartial') }}
        </div>

        <p v-if="product.description" class="product-card__desc">{{ product.description }}</p>

        <div class="product-card__meta">
          <div>
            <span>{{ $t('catalog.common.stock') }}</span>
            <strong>{{ product.quantity }}</strong>
          </div>
          <div>
            <span>{{ $t('catalog.common.cost') }}</span>
            <strong>{{ product.self_price }} ₸</strong>
          </div>
          <div>
            <span>{{ $t('catalog.common.price') }}</span>
            <strong class="product-card__price">{{ product.price }} ₸</strong>
          </div>
        </div>

        <div class="product-card__actions">
          <button type="button" class="product-card__action product-card__action--label" @click="printOneLabel(product)">
            <i class="mdi mdi-barcode"></i>
            {{ $t('catalog.products.printOneLabel') }}
          </button>
          <button type="button" class="product-card__action product-card__action--settle" @click="openSettlement(product)">
            <i class="mdi mdi-calculator-variant"></i>
            {{ $t('catalog.products.settlementAction') }}
          </button>
          <button type="button" class="product-card__action product-card__action--edit" @click="openEdit(product)">
            <i class="mdi mdi-pencil"></i>
            {{ $t('catalog.common.edit') }}
          </button>
        </div>
      </article>
    </section>

    <div v-else class="products-page__empty">
      <i class="mdi mdi-package-variant"></i>
      <p>{{ $t('catalog.products.noResults') }}</p>
      <span>{{ $t('catalog.products.noResultsHint') }}</span>
    </div>

    <div v-if="pageCount > 1" class="products-page__pagination">
      <p class="products-page__pagination-info">
        {{ $t('catalog.products.pageInfo', { page, count: pageCount, total }) }}
      </p>
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="7"
        color="#0b4f8a"
      />
    </div>

    <v-dialog width="480" v-model="deleteOpen">
      <v-card class="products-page__dialog">
        <v-card-title>{{ $t('catalog.products.deleteTitle') }}</v-card-title>
        <v-card-text>{{ $t('catalog.products.deleteWarning') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="error" @click="confirmDelete">{{ $t('catalog.common.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      width="560"
      v-model="settlementOpen"
      :persistent="settlementDeleteOpen"
      @click:outside="!settlementDeleteOpen && closeSettlement()"
    >
      <v-card class="products-page__dialog settlement-dialog">
        <header class="settlement-dialog__header">
          <div>
            <h2>{{ $t('catalog.products.settlementTitle') }}</h2>
            <p v-if="settlementProduct">{{ settlementProduct.name }}</p>
          </div>
          <span
            v-if="settlementProduct"
            class="settlement-dialog__status"
            :class="settlementProduct.settlement_done ? 'is-done' : 'is-open'"
          >
            {{ settlementProduct.settlement_done
              ? $t('catalog.products.settlementDone')
              : $t('catalog.products.settlementOpen') }}
          </span>
        </header>

        <v-card-text v-if="settlementProduct" class="settlement-dialog__body">
          <div class="settlement-dialog__grid">
            <div>
              <span>{{ $t('catalog.products.settlementHistoryQty') }}</span>
              <strong>{{ historyQty(settlementProduct) }} {{ getCountTypeLabel(settlementProduct.count_type) }}</strong>
            </div>
            <div>
              <span>{{ $t('catalog.common.cost') }}</span>
              <strong>{{ formatMoney(settlementProduct.self_price) }} ₸</strong>
            </div>
            <div>
              <span>{{ $t('catalog.products.settlementDueTotal') }}</span>
              <strong>{{ formatMoney(settlementProduct.supplier_due_total) }} ₸</strong>
            </div>
            <div>
              <span>{{ $t('catalog.products.settlementSettledQty') }}</span>
              <strong>{{ formatMoney(settledAmount(settlementProduct)) }} ₸</strong>
            </div>
            <div>
              <span>{{ $t('catalog.products.settlementRemaining') }}</span>
              <strong>{{ formatMoney(remainingAmount(settlementProduct)) }} ₸</strong>
            </div>
            <div>
              <span>{{ $t('catalog.products.settlementStockNow') }}</span>
              <strong>{{ currentStockQty(settlementProduct) }} {{ getCountTypeLabel(settlementProduct.count_type) }}</strong>
            </div>
            <div v-if="settlementProduct.supplier">
              <span>{{ $t('catalog.common.supplier') }}</span>
              <strong>{{ settlementProduct.supplier.name }}</strong>
            </div>
          </div>

          <section v-if="!settlementProduct.settlement_done || settlementEditId" class="settlement-dialog__form">
            <h3>
              {{ settlementEditId
                ? $t('catalog.products.settlementEditTitle')
                : $t('catalog.products.settlementConfirmTitle') }}
            </h3>
            <p>{{ $t('catalog.products.settlementPartialHint') }}</p>
            <v-text-field
              :value="settlementAmountText"
              type="text"
              inputmode="decimal"
              outlined
              dense
              hide-details="auto"
              :label="$t('catalog.products.settlementAmountLabel')"
              suffix="₸"
              :placeholder="formatMoney(settlementAmountLimit)"
              :hint="$t('catalog.products.settlementAmountHint', { amount: formatMoney(settlementAmountLimit) })"
              persistent-hint
              @input="onSettlementAmountInput"
            />
            <div class="settlement-dialog__quick">
              <button type="button" @click="fillSettlementRemaining">
                {{ $t('catalog.products.settlementFillRemaining') }}
              </button>
              <button v-if="settlementAmountText" type="button" @click="clearSettlementAmount">
                {{ $t('catalog.products.settlementClearAmount') }}
              </button>
            </div>
            <div class="settlement-dialog__amount">
              <span>{{ $t('catalog.products.settlementPayNow') }}</span>
              <strong>{{ formatMoney(parsedSettlementAmount) }} ₸</strong>
            </div>
            <v-text-field
              v-model="settlementNote"
              outlined
              dense
              hide-details
              :label="$t('catalog.products.settlementNote')"
            />
            <button
              v-if="settlementEditId"
              type="button"
              class="settlement-dialog__cancel-edit"
              @click="cancelSettlementEdit"
            >
              {{ $t('catalog.products.settlementCancelEdit') }}
            </button>
          </section>

          <section v-if="settlementHistory.length" class="settlement-dialog__history">
            <h3>{{ $t('catalog.products.settlementHistory') }}</h3>
            <ul>
              <li
                v-for="item in settlementHistory"
                :key="item.id"
                :class="{ 'is-editing': settlementEditId === item.id }"
              >
                <div>
                  <strong>{{ formatMoney(item.amount) }} ₸</strong>
                  <span v-if="item.quantity">
                    {{ item.quantity }} {{ getCountTypeLabel(settlementProduct.count_type) }}
                  </span>
                </div>
                <div class="settlement-dialog__history-row">
                  <small>
                    {{ formatSettlementDate(item.created_at) }}
                    <template v-if="item.user"> · {{ item.user.name }}</template>
                  </small>
                  <div class="settlement-dialog__history-actions">
                    <button type="button" @click="startSettlementEdit(item)">
                      <i class="mdi mdi-pencil-outline"></i>
                      {{ $t('catalog.common.edit') }}
                    </button>
                    <button type="button" class="is-danger" @click="askDeleteSettlement(item)">
                      <i class="mdi mdi-delete-outline"></i>
                      {{ $t('catalog.common.delete') }}
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </section>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="settlementSaving" @click="closeSettlement">{{ $t('catalog.common.close') }}</v-btn>
          <v-btn
            v-if="settlementProduct && (!settlementProduct.settlement_done || settlementEditId)"
            color="#0b4f8a"
            dark
            :loading="settlementSaving"
            @click="confirmSettlement"
          >
            {{ settlementEditId ? $t('catalog.common.save') : $t('catalog.products.settlementConfirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="420" v-model="settlementDeleteOpen">
      <v-card class="products-page__dialog">
        <v-card-title>{{ $t('catalog.products.settlementDeleteTitle') }}</v-card-title>
        <v-card-text>{{ $t('catalog.products.settlementDeleteWarning') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="settlementSaving" @click="settlementDeleteOpen = false">
            {{ $t('catalog.common.cancel') }}
          </v-btn>
          <v-btn color="error" :loading="settlementSaving" @click="confirmDeleteSettlement">
            {{ $t('catalog.common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="640" v-model="formOpen">
      <v-card class="products-page__dialog product-form-dialog">
        <header class="product-form-dialog__header">
          <div class="product-form-dialog__icon">
            <i :class="isEdit ? 'mdi mdi-pencil-outline' : 'mdi mdi-package-variant-plus'"></i>
          </div>
          <div>
            <h2>{{ isEdit ? $t('catalog.products.editTitle') : $t('catalog.products.createTitle') }}</h2>
            <p>{{ $t('catalog.products.barcodeHint') }}</p>
          </div>
        </header>
        <v-card-text class="product-form-dialog__body">
          <section class="product-form-section">
            <h3>{{ $t('catalog.products.formDetails') }}</h3>
            <v-switch
              v-model="hasProductPhoto"
              inset
              dense
              hide-details
              class="product-form-photo-switch"
              :label="$t('catalog.products.hasProductPhoto')"
              @change="toggleProductPhoto"
            />
            <div v-if="hasProductPhoto" class="product-photo-field">
              <div class="product-photo-field__preview">
                <img v-if="imagePreview" :src="imagePreview" :alt="form.name || $t('catalog.products.productPhoto')">
                <i v-else class="mdi mdi-image-outline"></i>
              </div>
              <div class="product-photo-field__copy">
                <strong>{{ $t('catalog.products.productPhoto') }}</strong>
                <span>{{ $t('catalog.products.photoHint') }}</span>
                <div class="product-photo-field__actions">
                  <button type="button" class="product-photo-field__upload" @click="$refs.productImageInput.click()">
                    <i class="mdi mdi-upload-outline"></i>
                    {{ imagePreview ? $t('catalog.products.changePhoto') : $t('catalog.products.uploadPhoto') }}
                  </button>
                  <button v-if="imagePreview" type="button" class="product-photo-field__remove" @click="removeImage">
                    {{ $t('catalog.products.removePhoto') }}
                  </button>
                </div>
              </div>
              <input
                ref="productImageInput"
                class="product-photo-field__input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                @change="onImageSelected"
              >
            </div>
            <div class="product-form-grid">
              <v-select
                v-model="form.category_id"
                :items="categories"
                item-text="name"
                item-value="id"
                :label="$t('catalog.common.category')"
                outlined
                dense
              />
              <v-text-field v-model="form.name" outlined dense :label="$t('catalog.common.name')" />
            </div>
            <v-select
              v-model="form.supplier_id"
              :items="suppliers"
              item-text="name"
              item-value="id"
              clearable
              :label="$t('catalog.common.supplier')"
              :hint="$t('catalog.products.supplierHint')"
              persistent-hint
              outlined
              dense
              class="product-form-supplier"
            />
            <v-text-field
              v-model.number="form.sort_order"
              outlined
              dense
              type="number"
              min="0"
              max="9999"
              prepend-inner-icon="mdi-sort-numeric-ascending"
              :label="$t('catalog.products.sortOrder')"
              :hint="$t('catalog.products.sortOrderHint')"
              persistent-hint
              class="product-form-sort-order"
            />
            <v-switch
              v-model="form.has_barcode"
              inset
              dense
              hide-details
              class="product-form-barcode-switch"
              :label="$t('catalog.products.hasBarcode')"
            />
            <v-text-field
              v-if="form.has_barcode"
              v-model.trim="form.barcode"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-barcode"
              :label="$t('catalog.products.barcode')"
            />
            <p v-else class="product-form-auto-hint">
              <i class="mdi mdi-barcode-scan"></i>
              {{ $t('catalog.products.barcodeAutoHint') }}
            </p>
          </section>

          <section class="product-form-section">
            <h3>{{ $t('catalog.products.formPricing') }}</h3>
            <div class="product-form-grid product-form-grid--three">
              <v-text-field v-model="form.quantity" outlined dense type="number" :label="$t('catalog.common.quantity')" />
              <v-text-field
                v-model="form.self_price"
                outlined
                dense
                type="number"
                :label="$t('catalog.common.cost')"
                @input="calcRetailPrice"
              />
              <v-text-field
                v-model="form.markup_percent"
                outlined
                dense
                type="number"
                :label="$t('catalog.products.markup')"
                @input="calcRetailPrice"
              />
            </div>
            <div class="product-form-grid">
              <v-text-field
                v-model="form.price"
                outlined
                dense
                type="number"
                :label="$t('catalog.common.retailPrice')"
                :hint="$t('catalog.products.retailRoundedHint')"
                persistent-hint
                @input="onRetailPriceInput"
              />
              <v-select
                v-model="form.count_type"
                :items="countTypes"
                item-text="label"
                item-value="value"
                :label="$t('catalog.products.measurementType')"
                outlined
                dense
              />
            </div>
          </section>

          <section class="product-form-section">
            <div class="product-form-section__head">
              <h3>{{ $t('catalog.products.formExpenses') }}</h3>
              <button type="button" class="product-form-add-row" @click="addExpenseRow">
                <i class="mdi mdi-plus"></i>
                {{ $t('catalog.products.addExpense') }}
              </button>
            </div>
            <p class="product-form-auto-hint">{{ $t('catalog.products.expensesHint') }}</p>
            <div v-if="form.expenses.length" class="product-form-rows">
              <div v-for="(row, index) in form.expenses" :key="`expense-${index}`" class="product-form-row">
                <v-text-field
                  v-model.trim="row.name"
                  outlined
                  dense
                  hide-details
                  :label="$t('catalog.products.expenseName')"
                  :placeholder="$t('catalog.products.expenseNamePlaceholder')"
                />
                <v-text-field
                  v-model.number="row.amount"
                  outlined
                  dense
                  hide-details
                  type="number"
                  min="0"
                  step="0.01"
                  :label="$t('catalog.products.expenseAmount')"
                  suffix="₸"
                />
                <button
                  type="button"
                  class="product-form-row__remove"
                  :title="$t('catalog.common.delete')"
                  @click="removeExpenseRow(index)"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <p v-else class="product-form-auto-hint">{{ $t('catalog.products.expensesEmpty') }}</p>
          </section>

          <section class="product-form-section">
            <div class="product-form-section__head">
              <h3>{{ $t('catalog.products.formByproducts') }}</h3>
              <button type="button" class="product-form-add-row" @click="addByproductRow">
                <i class="mdi mdi-plus"></i>
                {{ $t('catalog.products.addByproduct') }}
              </button>
            </div>
            <p class="product-form-auto-hint">{{ $t('catalog.products.byproductsHint') }}</p>
            <div v-if="form.byproducts.length" class="product-form-rows">
              <div
                v-for="(row, index) in form.byproducts"
                :key="`byproduct-${index}`"
                class="product-form-row product-form-row--waste"
              >
                <v-text-field
                  v-model.trim="row.name"
                  outlined
                  dense
                  hide-details
                  :label="$t('catalog.products.byproductName')"
                  :placeholder="$t('catalog.products.byproductNamePlaceholder')"
                />
                <v-text-field
                  v-model.number="row.quantity"
                  outlined
                  dense
                  hide-details
                  type="number"
                  min="0"
                  step="0.001"
                  :label="$t('catalog.common.quantity')"
                />
                <v-select
                  v-model="row.count_type"
                  :items="wasteCountTypes"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  :label="$t('catalog.products.measurementType')"
                />
                <button
                  type="button"
                  class="product-form-row__remove"
                  :title="$t('catalog.common.delete')"
                  @click="removeByproductRow(index)"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <p v-else class="product-form-auto-hint">{{ $t('catalog.products.byproductsEmpty') }}</p>
          </section>

          <section class="product-form-section">
            <div class="product-form-section__head">
              <h3>{{ $t('catalog.products.formWastes') }}</h3>
              <button type="button" class="product-form-add-row" @click="addWasteRow">
                <i class="mdi mdi-plus"></i>
                {{ $t('catalog.products.addWaste') }}
              </button>
            </div>
            <p class="product-form-auto-hint">{{ $t('catalog.products.wastesHint') }}</p>
            <div v-if="form.wastes.length" class="product-form-rows">
              <div v-for="(row, index) in form.wastes" :key="`waste-${index}`" class="product-form-row product-form-row--waste">
                <v-text-field
                  v-model.trim="row.name"
                  outlined
                  dense
                  hide-details
                  :label="$t('catalog.products.wasteName')"
                  :placeholder="$t('catalog.products.wasteNamePlaceholder')"
                />
                <v-text-field
                  v-model.number="row.quantity"
                  outlined
                  dense
                  hide-details
                  type="number"
                  min="0"
                  step="0.001"
                  :label="$t('catalog.common.quantity')"
                />
                <v-select
                  v-model="row.count_type"
                  :items="wasteCountTypes"
                  item-text="label"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                  :label="$t('catalog.products.measurementType')"
                />
                <button
                  type="button"
                  class="product-form-row__remove"
                  :title="$t('catalog.common.delete')"
                  @click="removeWasteRow(index)"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <p v-else class="product-form-auto-hint">{{ $t('catalog.products.wastesEmpty') }}</p>
          </section>

          <section class="product-form-section">
            <h3>{{ $t('catalog.products.formModifiers') }}</h3>
            <p class="product-form-auto-hint">{{ $t('catalog.products.modifiersHint') }}</p>
            <v-select
              v-if="allModifiers.length"
              v-model="form.modifier_ids"
              :items="allModifiers"
              item-text="label"
              item-value="id"
              :label="$t('catalog.products.selectModifiers')"
              outlined
              dense
              multiple
              chips
              deletable-chips
              clearable
            />
            <p v-else class="product-form-auto-hint">{{ $t('catalog.products.modifiersEmpty') }}</p>
          </section>

          <section class="product-form-section product-form-section--discount">
            <div class="product-form-section__head">
              <h3>{{ $t('catalog.products.formDiscount') }}</h3>
              <v-switch
                v-model="hasProductDiscount"
                inset
                dense
                hide-details
                :label="$t('catalog.products.applyProductDiscount')"
                @change="toggleProductDiscount"
              />
            </div>
            <div v-if="hasProductDiscount" class="product-form-grid product-form-grid--discount">
              <v-select
                v-model="form.product_discount_type"
                :items="productDiscountTypes"
                item-text="label"
                item-value="value"
                outlined
                dense
                :label="$t('catalog.products.productDiscount')"
              />
              <v-text-field
                v-model.number="form.product_discount_value"
                type="number"
                min="0"
                outlined
                dense
                :label="$t('catalog.products.discountValue')"
              />
            </div>
          </section>

          <section class="product-form-section product-form-section--last">
            <h3>{{ $t('catalog.products.formDescription') }}</h3>
            <v-textarea v-model="form.description" outlined dense :label="$t('catalog.common.description')" rows="3" hide-details />
          </section>
        </v-card-text>
        <v-card-actions class="product-form-dialog__actions">
          <v-spacer />
          <v-btn text @click="formOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="#0b4f8a" dark :loading="saving" @click="saveProduct">{{ $t('catalog.common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="labelsOpen" max-width="960" content-class="labels-dialog">
      <div class="labels-modal">
        <header class="labels-modal__header">
          <div>
            <h2>{{ $t('catalog.products.labelsTitle') }}</h2>
            <p>{{ isRollLabel ? $t('catalog.products.labelsHintRoll') : $t('catalog.products.labelsHint') }}</p>
          </div>
          <button type="button" class="labels-modal__close" @click="labelsOpen = false">
            <i class="mdi mdi-close"></i>
          </button>
        </header>

        <div class="labels-modal__settings">
          <v-select
            v-model="labelSize"
            :items="labelSizeOptions"
            item-text="label"
            item-value="value"
            outlined
            dense
            hide-details
            :label="$t('catalog.products.labelSize')"
          />
          <span>{{ $t('catalog.products.labelsPerPage', { count: labelFormat.perPage }) }}</span>
        </div>

        <div ref="labelsPrintable" class="labels-printable">
          <section
            v-for="(labels, pageIndex) in labelPages"
            :key="pageIndex"
            ref="labelPages"
            class="labels-sheet"
            :class="{ 'labels-sheet--roll': isRollLabel }"
            :style="labelSheetStyle"
          >
            <article v-for="product in labels" :key="product.id" class="product-label" :class="`product-label--${labelSize}`">
              <strong class="product-label__name">{{ product.name }}</strong>
              <div class="product-label__prices">
                <s v-if="labelDiscount(product)">{{ product.price }} ₸</s>
                <b>{{ labelPrice(product) }} ₸</b>
              </div>
              <div class="product-label__barcode-wrap">
                <svg :ref="`labelBarcode${pageIndex}-${product.id}`" class="product-label__barcode"></svg>
                <span>{{ product.barcode }}</span>
              </div>
            </article>
          </section>
        </div>

        <footer class="labels-modal__footer">
          <button type="button" class="labels-modal__btn labels-modal__btn--ghost" @click="labelsOpen = false">
            {{ $t('catalog.products.closeLabels') }}
          </button>
          <button type="button" class="labels-modal__btn labels-modal__btn--ghost" :disabled="downloadingLabels" @click="downloadLabels">
            <i class="mdi mdi-download-outline"></i>
            {{ $t('catalog.products.downloadLabels') }}
          </button>
          <button type="button" class="labels-modal__btn" @click="printLabels">
            <i class="mdi mdi-printer-outline"></i>
            {{ $t('catalog.products.printOrSavePdf') }}
          </button>
        </footer>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import JsBarcode from 'jsbarcode'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { getCategories } from '@/api/categories'
import { getModifiers } from '@/api/modifiers'
import {
  createProduct,
  createProductSettlement,
  deleteProduct,
  deleteProductSettlement,
  getProductSettlements,
  getProducts,
  updateProductSettlement,
  updateProduct,
} from '@/api/products'
import { getSuppliers } from '@/api/suppliers'
import { COUNT_TYPE_OPTIONS, getCountTypeLabel } from '@/utils/format'
import { LABEL_FORMATS } from '@/utils/labelFormats'
import { notify, notifyError } from '@/utils/notify'

const emptyExpenseRow = () => ({ name: '', amount: null })
const emptyWasteRow = () => ({ name: '', quantity: null, count_type: 'count' })
const emptyByproductRow = () => ({ name: '', quantity: null, count_type: 'kg' })

const emptyForm = () => ({
  category_id: '',
  supplier_id: null,
  name: '',
  sort_order: 0,
  barcode: '',
  has_barcode: false,
  image: null,
  remove_image: false,
  product_discount_type: 'percent',
  product_discount_value: null,
  quantity: '',
  self_price: '',
  markup_percent: '',
  price: '',
  count_type: '',
  description: '',
  modifier_ids: [],
  expenses: [],
  byproducts: [],
  wastes: [],
})

export default {
  name: 'ProductControl',
  data() {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      total: 0,
      categoryId: null,
      categories: [],
      suppliers: [],
      allModifiers: [],
      products: [],
      countTypes: COUNT_TYPE_OPTIONS,
      form: emptyForm(),
      formOpen: false,
      isEdit: false,
      productId: null,
      deleteOpen: false,
      saving: false,
      selectedProducts: {},
      labelsOpen: false,
      labelSize: 'roll30x20',
      hasProductDiscount: false,
      hasProductPhoto: false,
      imagePreview: null,
      downloadingLabels: false,
      syncingPrice: false,
      getCountTypeLabel,
      settlementOpen: false,
      settlementProduct: null,
      settlementHistory: [],
      settlementAmountText: '',
      settlementNote: '',
      settlementSaving: false,
      settlementEditId: null,
      settlementDeleteOpen: false,
      settlementDeleteItem: null,
    }
  },
  mounted() {
    this.loadCategories()
    this.loadSuppliers()
    this.loadModifiers()
    this.loadProducts()
  },
  watch: {
    page() {
      this.loadProducts()
    },
    search() {
      this.debouncedSearch()
    },
    labelSize() {
      if (this.labelsOpen) {
        this.$nextTick(this.renderLabelBarcodes)
      }
    },
    settlementOpen(open) {
      if (!open) this.closeSettlement()
    },
  },
  computed: {
    parsedSettlementAmount() {
      return this.parseSettlementAmount(this.settlementAmountText)
    },
    settlementAmountLimit() {
      if (!this.settlementProduct) return 0
      const remaining = this.remainingAmount(this.settlementProduct)
      if (!this.settlementEditId) return remaining
      const item = this.settlementHistory.find((row) => row.id === this.settlementEditId)
      return Math.round((remaining + (Number(item && item.amount) || 0)) * 100) / 100
    },
    selectedCount() {
      return Object.keys(this.selectedProducts).length
    },
    labelProducts() {
      return Object.values(this.selectedProducts).filter((product) => product.barcode)
    },
    productDiscountTypes() {
      return [
        { value: 'percent', label: this.$t('catalog.products.discountTypePercent') },
        { value: 'amount', label: this.$t('catalog.products.discountTypeAmount') },
      ]
    },
    wasteCountTypes() {
      return [
        { value: 'count', label: this.$t('catalog.products.unitPcs') },
        { value: 'kg', label: this.$t('catalog.products.unitKg') },
      ]
    },
    labelSizeOptions() {
      return [
        { value: 'roll30x20', label: this.$t('catalog.products.labelSizeRoll30x20') },
        { value: 'roll25x20', label: this.$t('catalog.products.labelSizeRoll25x20') },
        { value: 'roll25x15', label: this.$t('catalog.products.labelSizeRoll25x15') },
        { value: 'roll30x25', label: this.$t('catalog.products.labelSizeRoll30x25') },
        { value: 'mini', label: this.$t('catalog.products.labelSizeMini') },
        { value: 'compact', label: this.$t('catalog.products.labelSizeCompact') },
        { value: 'standard', label: this.$t('catalog.products.labelSizeStandard') },
        { value: 'large', label: this.$t('catalog.products.labelSizeLarge') },
      ]
    },
    labelFormat() {
      const format = LABEL_FORMATS[this.labelSize] || LABEL_FORMATS.standard
      return { ...format, perPage: format.columns * format.rows }
    },
    isRollLabel() {
      return !!this.labelFormat.roll
    },
    barcodeHeight() {
      const sizes = {
        mini: 16,
        compact: 24,
        standard: 38,
        large: 48,
        roll25x15: 10,
        roll25x20: 14,
        roll30x20: 14,
        roll30x25: 18,
      }
      return sizes[this.labelSize] || 38
    },
    barcodeWidth() {
      const sizes = {
        mini: 0.75,
        compact: 1,
        standard: 1.35,
        large: 1.75,
        roll25x15: 0.7,
        roll25x20: 0.8,
        roll30x20: 0.85,
        roll30x25: 0.9,
      }
      return sizes[this.labelSize] || 1.35
    },
    labelPages() {
      const pages = []
      const { perPage } = this.labelFormat
      for (let index = 0; index < this.labelProducts.length; index += perPage) {
        pages.push(this.labelProducts.slice(index, index + perPage))
      }
      return pages
    },
    labelSheetStyle() {
      const format = this.labelFormat
      const pageHeight = format.rows * format.height + (format.rows - 1) * (format.gap || 0)
      return {
        '--label-columns': format.columns,
        '--label-width': `${format.width}mm`,
        '--label-height': `${format.height}mm`,
        '--label-gap': `${format.gap || 0}mm`,
        '--sheet-width': `${format.sheetWidth}mm`,
        '--sheet-height': format.roll ? `${pageHeight}mm` : '281mm',
      }
    },
  },
  methods: {
    formatMoney(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
    },
    parseSettlementAmount(value) {
      const raw = String(value || '')
        .replace(/\s/g, '')
        .replace(',', '.')
        .replace(/[^\d.]/g, '')
      if (!raw) return 0
      const n = Number(raw)
      return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0
    },
    formatSettlementInput(value) {
      const next = String(value == null ? '' : value)
      const normalized = next.replace(/[^\d.,]/g, '').replace(',', '.')
      const parts = normalized.split('.')
      const intDigits = (parts[0] || '').replace(/\D/g, '')
      const grouped = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      if (parts.length < 2) return grouped
      const frac = parts.slice(1).join('').replace(/\D/g, '').slice(0, 2)
      return `${grouped}.${frac}`
    },
    onSettlementAmountInput(value) {
      const next = typeof value === 'string' ? value : (value && value.target && value.target.value) || ''
      this.settlementAmountText = this.formatSettlementInput(next)
    },
    fillSettlementRemaining() {
      this.settlementAmountText = this.formatSettlementInput(this.settlementAmountLimit)
    },
    clearSettlementAmount() {
      this.settlementAmountText = ''
    },
    applySettlementResult(data) {
      if (data.product) {
        this.settlementProduct = data.product
        this.patchProductInList(data.product)
      }
      if (Array.isArray(data.product && data.product.settlements)) {
        this.settlementHistory = data.product.settlements
      } else if (data.settlement) {
        const index = this.settlementHistory.findIndex((row) => row.id === data.settlement.id)
        if (index === -1) {
          this.settlementHistory = [data.settlement, ...this.settlementHistory]
        } else {
          this.$set(this.settlementHistory, index, data.settlement)
        }
      }
    },
    historyQty(product) {
      if (!product) return 0
      const history = product.history_quantity
      if (history !== null && history !== undefined && history !== '') {
        return Number(history) || 0
      }
      return Number(product.quantity) || 0
    },
    currentStockQty(product) {
      if (!product) return 0
      return Number(product.quantity) || 0
    },
    dueTotal(product) {
      if (!product) return 0
      const due = Number(product.supplier_due_total)
      if (Number.isFinite(due)) return Math.max(0, due)
      const cost = Number(product.self_price) || 0
      return Math.round(this.historyQty(product) * cost * 100) / 100
    },
    settledAmount(product) {
      if (!product) return 0
      const settled = Number(product.settled_amount)
      if (Number.isFinite(settled) && !Number.isNaN(settled)) return Math.max(0, settled)
      return 0
    },
    remainingAmount(product) {
      if (!product) return 0
      const remaining = Number(product.settlement_remaining_amount)
      if (Number.isFinite(remaining) && !Number.isNaN(remaining)) {
        return Math.max(0, remaining)
      }
      return Math.max(0, Math.round((this.dueTotal(product) - this.settledAmount(product)) * 100) / 100)
    },
    settlementProgress(product) {
      const due = this.dueTotal(product)
      if (due <= 0) return 0
      return this.settledAmount(product) / due
    },
    formatSettlementDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    async openSettlement(product) {
      this.settlementProduct = { ...product }
      this.settlementHistory = []
      this.settlementNote = ''
      this.settlementAmountText = ''
      this.settlementEditId = null
      this.settlementDeleteOpen = false
      this.settlementDeleteItem = null
      this.settlementOpen = true

      try {
        const data = await getProductSettlements(product.id)
        if (data.product) {
          this.settlementProduct = data.product
          this.patchProductInList(data.product)
        }
        this.settlementHistory = Array.isArray(data.settlements) ? data.settlements : []
      } catch (error) {
        notifyError(error)
      }
    },
    closeSettlement() {
      this.settlementOpen = false
      this.settlementProduct = null
      this.settlementHistory = []
      this.settlementAmountText = ''
      this.settlementNote = ''
      this.settlementSaving = false
      this.settlementEditId = null
      this.settlementDeleteOpen = false
      this.settlementDeleteItem = null
    },
    startSettlementEdit(item) {
      if (!item) return
      this.settlementEditId = item.id
      this.settlementAmountText = this.formatSettlementInput(item.amount)
      this.settlementNote = item.note || ''
    },
    cancelSettlementEdit() {
      this.settlementEditId = null
      this.settlementAmountText = ''
      this.settlementNote = ''
    },
    askDeleteSettlement(item) {
      this.settlementDeleteItem = item
      this.settlementDeleteOpen = true
    },
    async confirmDeleteSettlement() {
      if (!this.settlementProduct || !this.settlementDeleteItem) return
      this.settlementSaving = true
      try {
        const data = await deleteProductSettlement(this.settlementProduct.id, this.settlementDeleteItem.id)
        if (this.settlementEditId === this.settlementDeleteItem.id) {
          this.cancelSettlementEdit()
        }
        this.settlementHistory = this.settlementHistory.filter((row) => row.id !== this.settlementDeleteItem.id)
        this.applySettlementResult(data)
        this.settlementDeleteOpen = false
        this.settlementDeleteItem = null
        notify(data.message || this.$t('catalog.products.settlementDeleted'))
      } catch (error) {
        notifyError(error)
      } finally {
        this.settlementSaving = false
      }
    },
    patchProductInList(product) {
      if (!product || !product.id) return
      const index = this.products.findIndex((item) => item.id === product.id)
      if (index === -1) return
      this.$set(this.products, index, {
        ...this.products[index],
        ...product,
      })
    },
    async confirmSettlement() {
      if (!this.settlementProduct) return

      const amount = this.parsedSettlementAmount
      const remaining = this.settlementAmountLimit
      if (!Number.isFinite(amount) || amount <= 0) {
        notify(this.$t('catalog.products.settlementAmountInvalid'), 'warning')
        return
      }
      if (amount > remaining + 0.01) {
        notify(this.$t('catalog.products.settlementAmountTooMuch', { max: this.formatMoney(remaining) }), 'warning')
        return
      }

      this.settlementSaving = true
      try {
        const payload = {
          amount,
          note: this.settlementNote || null,
        }
        const data = this.settlementEditId
          ? await updateProductSettlement(this.settlementProduct.id, this.settlementEditId, payload)
          : await createProductSettlement(this.settlementProduct.id, payload)
        this.applySettlementResult(data)
        this.cancelSettlementEdit()
        notify(data.message || this.$t('catalog.products.settlementSaved'))
      } catch (error) {
        notifyError(error)
      } finally {
        this.settlementSaving = false
      }
    },
    isSelected(id) {
      return !!this.selectedProducts[id]
    },
    labelDiscount(product) {
      if (!product.product_discount_type || !Number(product.product_discount_value)) return null
      return {
        type: product.product_discount_type,
        value: Number(product.product_discount_value),
      }
    },
    labelPrice(product) {
      const price = Number(product.price) || 0
      const discount = this.labelDiscount(product)
      if (!discount) return price

      const reduction = discount.type === 'percent'
        ? price * (Number(discount.value) / 100)
        : Number(discount.value)
      return Math.max(0, Math.round((price - reduction) * 100) / 100)
    },
    toggleProductDiscount(enabled) {
      if (!enabled) {
        this.form.product_discount_value = null
      }
    },
    toggleProductSelection(product) {
      if (this.selectedProducts[product.id]) {
        this.$delete(this.selectedProducts, product.id)
      } else {
        this.$set(this.selectedProducts, product.id, { ...product })
      }
    },
    printOneLabel(product) {
      if (!this.selectedProducts[product.id]) {
        this.$set(this.selectedProducts, product.id, { ...product })
      }
      this.openLabels()
    },
    openLabels() {
      if (!this.selectedCount) {
        notify(this.$t('catalog.products.selectForLabels'), 'warning')
        return
      }
      if (!this.labelProducts.length) {
        notify(this.$t('catalog.products.barcodeMissing'), 'warning')
        return
      }

      const skipped = this.selectedCount - this.labelProducts.length
      if (skipped) {
        notify(this.$t('catalog.products.barcodeSkipped', { count: skipped }), 'warning')
      }

      this.labelsOpen = true
      this.$nextTick(this.renderLabelBarcodes)
    },
    renderLabelBarcodes() {
      this.labelPages.forEach((labels, pageIndex) => {
        labels.forEach((product) => {
          const ref = this.$refs[`labelBarcode${pageIndex}-${product.id}`]
          const svg = Array.isArray(ref) ? ref[0] : ref
          if (!svg) return

          JsBarcode(svg, String(product.barcode), {
            format: 'CODE128',
            displayValue: false,
            margin: 0,
            height: this.barcodeHeight,
            width: this.barcodeWidth,
          })
        })
      })
    },
    async downloadLabels() {
      const pages = this.$refs.labelPages
      const printablePages = Array.isArray(pages) ? pages : [pages]
      if (!printablePages[0]) return

      this.downloadingLabels = true
      try {
        const format = this.labelFormat
        const pageW = format.roll ? format.sheetWidth : 210
        const pageH = format.roll
          ? format.rows * format.height + (format.rows - 1) * (format.gap || 0)
          : 297
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [pageW, pageH] })

        for (let index = 0; index < printablePages.length; index += 1) {
          const canvas = await html2canvas(printablePages[index], {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
          })
          if (index) pdf.addPage([pageW, pageH], 'portrait')
          pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageW, pageH)
        }

        const suffix = format.roll ? `${format.width}x${format.height}` : 'a4'
        pdf.save(`sauda-barcodes-${suffix}-${new Date().toISOString().slice(0, 10)}.pdf`)
      } catch (error) {
        notifyError(error)
      } finally {
        this.downloadingLabels = false
      }
    },
    labelPrintCss() {
      const format = this.labelFormat
      const pageH = format.rows * format.height + (format.rows - 1) * (format.gap || 0)
      const page = format.roll
        ? `@page{size:${format.sheetWidth}mm ${pageH}mm;margin:0}`
        : '@page{size:A4;margin:8mm}'
      const sheet = format.roll
        ? `.labels-sheet{width:${format.sheetWidth}mm;min-height:${pageH}mm;margin:0;gap:${format.gap}mm}`
        : '.labels-sheet{width:194mm;min-height:281mm;justify-content:space-between}'
      return `${page}body{margin:0;font-family:Arial,sans-serif;color:#152033}.labels-sheet{display:grid;grid-template-columns:repeat(var(--label-columns),var(--label-width));grid-auto-rows:var(--label-height);align-content:start;page-break-after:always}${sheet}.product-label{box-sizing:border-box;padding:3mm;border:1px dashed #9fb0c1;border-radius:2mm;display:grid;justify-items:center;align-content:center;gap:2px;text-align:center;break-inside:avoid}.product-label__name{display:-webkit-box;overflow:hidden;max-width:100%;font-size:14px;font-weight:700;line-height:1.15;-webkit-box-orient:vertical;-webkit-line-clamp:2}.product-label__prices{display:flex;align-items:baseline;justify-content:center;gap:4px}.product-label s{color:#6b7a90;font-size:10px}.product-label b{color:#b42318;font-size:20px}.product-label__barcode-wrap{display:grid;width:100%;justify-items:center;gap:1px;padding:1.5mm 1mm 1mm;border-radius:1.5mm;background:#f5f7fa}.product-label svg{max-width:100%;height:38px}.product-label span{color:#506176;font-size:9px;letter-spacing:.08em}.product-label--mini,.product-label--roll25x15,.product-label--roll25x20,.product-label--roll30x20,.product-label--roll30x25{padding:.7mm;gap:0}.product-label--mini .product-label__name,.product-label--roll25x15 .product-label__name,.product-label--roll25x20 .product-label__name,.product-label--roll30x20 .product-label__name,.product-label--roll30x25 .product-label__name{font-size:7px;-webkit-line-clamp:1}.product-label--mini b,.product-label--roll25x15 b,.product-label--roll25x20 b,.product-label--roll30x20 b,.product-label--roll30x25 b{font-size:11px}.product-label--mini s,.product-label--roll25x15 s,.product-label--roll25x20 s,.product-label--roll30x20 s,.product-label--roll30x25 s{font-size:6px}.product-label--mini svg,.product-label--roll25x15 svg{height:10px}.product-label--roll25x20 svg,.product-label--roll30x20 svg{height:14px}.product-label--roll30x25 svg{height:18px}.product-label--mini span,.product-label--roll25x15 span,.product-label--roll25x20 span,.product-label--roll30x20 span,.product-label--roll30x25 span{font-size:5px}.product-label--compact{padding:2mm}.product-label--compact .product-label__name{font-size:11px}.product-label--compact b{font-size:16px}.product-label--compact svg{height:24px}@media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}.labels-sheet:last-child{page-break-after:auto}}`
    },
    printLabels() {
      const source = this.$refs.labelsPrintable
      if (!source) return

      const printWindow = window.open('', '_blank', 'width=960,height=720')
      if (!printWindow) {
        notify('Разрешите всплывающие окна для печати наклеек', 'warning')
        return
      }

      printWindow.document.write(`<!doctype html><html><head><title>Sauda labels</title><style>${this.labelPrintCss()}</style></head><body>${source.outerHTML}</body></html>`)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    },
    calcRetailPrice() {
      if (this.syncingPrice) return
      const cost = Number(this.form.self_price)
      const percent = Number(this.form.markup_percent)

      if (!cost || Number.isNaN(cost) || this.form.markup_percent === '' || Number.isNaN(percent)) {
        return
      }

      this.syncingPrice = true
      this.form.price = Math.round(cost * (1 + percent / 100))
      this.$nextTick(() => {
        this.syncingPrice = false
      })
    },
    onRetailPriceInput() {
      if (this.syncingPrice) return
      this.syncingPrice = true
      const markup = this.markupFromPrices(this.form.self_price, this.form.price)
      if (markup !== '') this.form.markup_percent = markup
      this.$nextTick(() => {
        this.syncingPrice = false
      })
    },
    markupFromPrices(selfPrice, price) {
      const cost = Number(selfPrice)
      const retail = Number(price)
      if (!cost || !retail || Number.isNaN(cost) || Number.isNaN(retail)) return ''
      return Math.round(((retail - cost) / cost) * 100)
    },
    async loadCategories() {
      try {
        this.categories = await getCategories()
      } catch (error) {
        notifyError(error)
      }
    },
    async loadSuppliers() {
      try {
        this.suppliers = await getSuppliers()
      } catch (error) {
        this.suppliers = []
      }
    },
    addExpenseRow() {
      this.form.expenses.push(emptyExpenseRow())
    },
    removeExpenseRow(index) {
      this.form.expenses.splice(index, 1)
    },
    addWasteRow() {
      this.form.wastes.push(emptyWasteRow())
    },
    removeWasteRow(index) {
      this.form.wastes.splice(index, 1)
    },
    addByproductRow() {
      this.form.byproducts.push(emptyByproductRow())
    },
    removeByproductRow(index) {
      this.form.byproducts.splice(index, 1)
    },
    async loadModifiers() {
      try {
        const list = await getModifiers()
        this.allModifiers = (Array.isArray(list) ? list : []).map((item) => ({
          id: item.id,
          label: `${item.name} (+${Number(item.price) || 0} ₸)`,
          is_active: item.is_active,
        }))
      } catch (error) {
        this.allModifiers = []
      }
    },
    async loadProducts() {
      try {
        const params = {
          page: this.page,
          stock: 'stock',
        }

        if (this.search) {
          params.search = this.search
        } else if (this.categoryId) {
          params.category_id = this.categoryId
        }

        const data = await getProducts(params)
        this.products = data.data || []
        this.pageCount = Number(data.last_page) || 0
        this.total = Number(data.total) || 0

        if (this.page > this.pageCount && this.pageCount > 0) {
          this.page = this.pageCount
        }
      } catch (error) {
        notifyError(error)
      }
    },
    selectCategory(id) {
      this.categoryId = id
      this.search = ''
      this.resetToFirstPage()
    },
    resetToFirstPage() {
      if (this.page !== 1) {
        this.page = 1
      } else {
        this.loadProducts()
      }
    },
    debouncedSearch: debounce(function () {
      this.resetToFirstPage()
    }, 500),
    openCreate() {
      this.isEdit = false
      this.productId = null
      this.form = emptyForm()
      this.hasProductDiscount = false
      this.hasProductPhoto = false
      this.imagePreview = null
      this.formOpen = true
    },
    openEdit(product) {
      this.isEdit = true
      this.productId = product.id
      this.form = {
        category_id: product.category_id,
        supplier_id: product.supplier_id || null,
        name: product.name,
        sort_order: Number(product.sort_order) || 0,
        barcode: product.barcode || '',
        has_barcode: !!product.barcode,
        image: null,
        remove_image: false,
        product_discount_type: product.product_discount_type || 'percent',
        product_discount_value: product.product_discount_value || null,
        quantity: product.quantity,
        self_price: product.self_price,
        markup_percent: this.markupFromPrices(product.self_price, product.price),
        price: product.price,
        count_type: product.count_type,
        description: product.description,
        modifier_ids: Array.isArray(product.modifiers)
          ? product.modifiers.map((item) => item.id)
          : [],
        expenses: Array.isArray(product.expenses)
          ? product.expenses.map((item) => ({
            name: item.name || '',
            amount: item.amount != null ? Number(item.amount) : null,
          }))
          : [],
        byproducts: Array.isArray(product.byproducts)
          ? product.byproducts.map((item) => ({
            name: item.name || '',
            quantity: item.quantity != null ? Number(item.quantity) : null,
            count_type: item.count_type === 'count' ? 'count' : 'kg',
          }))
          : [],
        wastes: Array.isArray(product.wastes)
          ? product.wastes.map((item) => ({
            name: item.name || '',
            quantity: item.quantity != null ? Number(item.quantity) : null,
            count_type: item.count_type === 'kg' ? 'kg' : 'count',
          }))
          : [],
      }
      this.hasProductDiscount = !!product.product_discount_value
      this.hasProductPhoto = !!product.image_url
      this.imagePreview = product.image_url || null
      this.formOpen = true
    },
    askDelete(product) {
      this.productId = product.id
      this.deleteOpen = true
    },
    async saveProduct() {
      if (!this.form.self_price) {
        notify(this.$t('catalog.products.enterCost'), 'warning')
        return
      }
      if (this.form.price === '' || this.form.price === null) {
        notify(this.$t('catalog.products.enterPrice'), 'warning')
        return
      }

      this.saving = true
      try {
        const payload = { ...this.form }
        if (!this.hasProductDiscount) {
          payload.product_discount_type = null
          payload.product_discount_value = null
        }
        delete payload.markup_percent
        payload.sort_order = Math.min(9999, Math.max(0, parseInt(payload.sort_order, 10) || 0))
        payload.expenses = (this.form.expenses || [])
          .map((row) => ({
            name: String((row && row.name) || '').trim(),
            amount: Number(row && row.amount),
          }))
          .filter((row) => row.name && row.amount >= 0)
        payload.byproducts = (this.form.byproducts || [])
          .map((row) => ({
            name: String((row && row.name) || '').trim(),
            quantity: Number(row && row.quantity),
            count_type: (row && row.count_type) === 'count' ? 'count' : 'kg',
          }))
          .filter((row) => row.name && row.quantity >= 0)
        payload.wastes = (this.form.wastes || [])
          .map((row) => ({
            name: String((row && row.name) || '').trim(),
            quantity: Number(row && row.quantity),
            count_type: (row && row.count_type) === 'kg' ? 'kg' : 'count',
          }))
          .filter((row) => row.name && row.quantity >= 0)
        const result = this.isEdit
          ? await updateProduct(this.productId, payload)
          : await createProduct(payload)

        if (this.isEdit && this.selectedProducts[this.productId]) {
          this.$set(this.selectedProducts, this.productId, { ...result })
        }
        notify(result.message || this.$t('catalog.common.saved'))
        this.formOpen = false
        this.form = emptyForm()
        this.imagePreview = null
        await this.loadProducts()
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
    onImageSelected(event) {
      const [file] = event.target.files || []
      if (!file) return
      if (file.size > 10 * 1024 * 1024) {
        notify(this.$t('catalog.products.photoTooLarge'), 'warning')
        event.target.value = ''
        return
      }
      this.form.image = file
      this.form.remove_image = false
      this.imagePreview = URL.createObjectURL(file)
    },
    removeImage() {
      this.form.image = null
      this.form.remove_image = true
      this.imagePreview = null
      this.hasProductPhoto = false
      if (this.$refs.productImageInput) {
        this.$refs.productImageInput.value = ''
      }
    },
    toggleProductPhoto(enabled) {
      if (!enabled && this.imagePreview) {
        this.removeImage()
      }
    },
    async confirmDelete() {
      try {
        const result = await deleteProduct(this.productId)
        notify(result.message || this.$t('catalog.products.deleted'), 'warning')
        this.deleteOpen = false
        await this.loadProducts()
      } catch (error) {
        notifyError(error)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.products-page {
  --ink: #152033;
  --muted: #6b7a90;
  --accent: #0b4f8a;
  --line: #d9e2ec;
  --card: #ffffff;

  padding: 28px 32px 40px;
  color: var(--ink);
}

.products-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 28px;
}

.products-page__title {
  margin: 0;
  font-size: 28px;
  font-weight: 750;
  letter-spacing: -0.02em;
}

.products-page__subtitle {
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 14px;
}

.products-page__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.products-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;

  .mdi {
    font-size: 18px;
  }

  &--primary {
    background: var(--accent);
    color: #fff;

    &:hover {
      background: #093f6d;
    }
  }

  &--ghost {
    background: #fff;
    border-color: var(--line);
    color: var(--ink);

    &:hover {
      border-color: #b7c6d6;
    }
  }

  &--labels {
    background: #0b4f8a;
    border-color: #0b4f8a;
    color: #fff;

    em {
      min-width: 18px;
      padding: 1px 6px;
      border-radius: 999px;
      background: #fff;
      color: #0b4f8a;
      font-style: normal;
      font-size: 12px;
      line-height: 18px;
      text-align: center;
    }

    &:hover {
      background: #093f6d;
    }
  }
}

.products-page__section {
  margin-bottom: 22px;
}

.products-page__section-head h2 {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.products-page__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.products-page__chip {
  padding: 8px 14px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  color: var(--ink);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9eb6cc;
  }

  &--active {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
  }
}

.products-page__search {
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
}

.products-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: #b7c6d6;
    transform: translateY(-2px);
  }

  &--settled {
    border-color: #9dceb0;
    background: linear-gradient(180deg, #f4fbf6 0%, #fff 48%);
  }
}

.product-card__delete {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid #f0c4c4;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  color: #c62828;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);

  &:hover {
    background: #fdecec;
    border-color: #e59a9a;
  }
}

.product-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;

  &--done {
    background: #e5f6eb;
    color: #1f7a45;
  }

  &--partial {
    background: #fff4e5;
    color: #a05a00;
  }
}

.settlement-dialog__header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  padding: 20px 20px 0;

  h2 {
    margin: 0;
    font-size: 20px;
  }

  p {
    margin: 4px 0 0;
    color: var(--muted);
  }
}

.settlement-dialog__status {
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;

  &.is-done {
    background: #e5f6eb;
    color: #1f7a45;
  }

  &.is-open {
    background: #eef4fa;
    color: #0b4f8a;
  }
}

.settlement-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.settlement-dialog__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border: 1px solid var(--line);
    border-radius: 12px;
    background: #f8fafc;
  }

  span {
    font-size: 11px;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  strong {
    font-size: 15px;
  }
}

.settlement-dialog__form {
  display: flex;
  flex-direction: column;
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 15px;
  }

  p {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
  }
}

.settlement-dialog__quick {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  button {
    padding: 6px 10px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: #fff;
    font-size: 12px;
    font-weight: 650;
    cursor: pointer;
  }
}

.settlement-dialog__cancel-edit {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
}

.settlement-dialog__amount {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  background: #eef4fa;
  font-size: 14px;

  strong {
    color: #0b4f8a;
    font-size: 16px;
  }
}

.settlement-dialog__history {
  h3 {
    margin: 0 0 8px;
    font-size: 15px;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  li {
    padding: 10px 12px;
    border: 1px solid var(--line);
    border-radius: 10px;

    &.is-editing {
      border-color: #8fb4d4;
      background: #f4f8fc;
    }
  }

  div {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  small {
    color: var(--muted);
  }
}

.settlement-dialog__history-row {
  align-items: center;
  margin-top: 6px;
}

.settlement-dialog__history-actions {
  display: flex;
  gap: 8px;

  button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: #fff;
    font-size: 12px;
    font-weight: 650;
    cursor: pointer;
  }

  .is-danger {
    color: #c62828;
    border-color: #f0c4c4;
  }
}

.product-card__image {
  height: 150px;
  overflow: hidden;
  margin: -18px -18px 0;
  border-radius: 15px 15px 10px 10px;
  background: #edf2f6;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.product-card__top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  padding-right: 36px;
}

.product-card__name {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
}

.product-card__unit {
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 8px;
  background: #eef4fa;
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
}

.product-card__desc {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-card__meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding-top: 4px;

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  span {
    font-size: 11px;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  strong {
    font-size: 14px;
  }
}

.product-card__price {
  color: var(--accent);
}

.product-card__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: auto;
}

.product-card__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: #fff;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;

  &--label {
    grid-column: 1 / -1;
    background: #edf6fd;
    border-color: #b8d2e8;
    color: var(--accent);
  }

  &--settle {
    background: #f3faf5;
    border-color: #b7d9c2;
    color: #1f7a45;
  }

  &--settle:hover {
    border-color: #7fb892;
    background: #e8f6ec;
  }

  &--edit:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
}

.products-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  border: 1px dashed var(--line);
  border-radius: 16px;
  background: #fff;
  color: var(--muted);
  text-align: center;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: var(--ink);
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
  }
}

.products-page__pagination {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 28px;
}

.products-page__pagination-info {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
}

.products-page__dialog {
  border-radius: 16px !important;
}

.product-form-dialog {
  overflow: hidden;
  background: #fff;

  ::v-deep .v-text-field__details {
    min-height: 0;
    margin-bottom: 0;
  }

  ::v-deep .v-input--is-focused .v-input__slot {
    box-shadow: 0 0 0 3px rgba(11, 79, 138, .10);
  }

  ::v-deep .v-input__slot {
    min-height: 42px !important;
    border-radius: 10px !important;
    transition: box-shadow .18s ease;
  }
}

.product-form-dialog__header {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #e8eef3;

  h2,
  p {
    margin: 0;
  }

  h2 {
    color: var(--ink);
    font-size: 20px;
    letter-spacing: -.02em;
  }

  p {
    margin-top: 3px;
    color: var(--muted);
    font-size: 12px;
  }
}

.product-form-dialog__icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 12px;
  background: #e6f1fa;
  color: var(--accent);
  font-size: 21px;
}

.product-form-dialog__body {
  max-height: min(65vh, 650px);
  padding: 18px 24px 8px !important;
  overflow-y: auto;
}

.product-form-section {
  margin-bottom: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid #edf1f5;

  h3 {
    margin: 0 0 12px;
    color: #3d4f63;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .07em;
    text-transform: uppercase;
  }

  &--discount {
    padding: 14px 16px;
    border: 0;
    border-radius: 12px;
    background: #f3f8fc;
  }

  &--last {
    margin-bottom: 0;
    border-bottom: 0;
  }
}

.product-form-add-row {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #d9e2ec;
  border-radius: 10px;
  background: #fff;
  color: #0b4f8a;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  .mdi {
    font-size: 16px;
  }

  &:hover {
    border-color: #0b4f8a;
    background: #eef4fa;
  }
}

.product-form-rows {
  display: grid;
  gap: 10px;
}

.product-form-row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.9fr) 40px;
  gap: 8px;
  align-items: center;

  &--waste {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.7fr) minmax(0, 0.7fr) 40px;
  }
}

.product-form-row__remove {
  width: 36px;
  height: 36px;
  border: 1px solid #f0b4ae;
  border-radius: 10px;
  background: #fff5f4;
  color: #b42318;
  display: grid;
  place-items: center;
  cursor: pointer;

  .mdi {
    font-size: 18px;
  }
}

.product-form-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  h3 {
    margin-bottom: 0;
  }

  ::v-deep .v-input {
    margin-top: 0;
    padding-top: 0;
  }

  ::v-deep .v-label {
    color: #405168;
    font-size: 12px;
  }
}

.product-photo-field {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 16px;
  padding: 12px;
  border: 1px dashed #b8c8d6;
  border-radius: 12px;
  background: #f8fafc;
}

.product-photo-field__preview {
  display: grid;
  width: 72px;
  height: 72px;
  flex: 0 0 72px;
  overflow: hidden;
  place-items: center;
  border-radius: 10px;
  background: #e7eef5;
  color: #8092a5;
  font-size: 28px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.product-photo-field__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 3px;

  strong {
    color: #30445a;
    font-size: 13px;
  }

  > span {
    color: var(--muted);
    font-size: 11px;
  }
}

.product-photo-field__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.product-photo-field__upload,
.product-photo-field__remove {
  border: 0;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.product-photo-field__upload {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 9px;
  background: #dfeef9;
  color: var(--accent);
}

.product-photo-field__remove {
  padding: 7px 4px;
  background: transparent;
  color: #b42318;
}

.product-photo-field__input {
  display: none;
}

.product-form-sort-order {
  margin-bottom: 10px;
}

.product-form-barcode-switch,
.product-form-photo-switch {
  margin: 0 0 8px !important;
  padding: 0 !important;

  ::v-deep .v-label {
    color: #405168;
    font-size: 12px;
    font-weight: 650;
  }
}

.product-form-auto-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
  padding: 9px 10px;
  border-radius: 9px;
  background: #f1f6fa;
  color: #61758a;
  font-size: 12px;

  .mdi {
    color: var(--accent);
    font-size: 17px;
  }
}

.product-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 12px;

  &--three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &--discount {
    margin-top: 12px;
  }
}

.product-form-dialog__actions {
  padding: 14px 24px 20px !important;
  border-top: 1px solid #e8eef3;
}

.products-page__labels-bar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin: 0 0 18px;
  padding: 12px 14px;
  border: 1px solid #8fb6d6;
  border-radius: 14px;
  background: #0b4f8a;
  color: #fff;
  font-size: 14px;
  font-weight: 700;

  button {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 14px;
    border: 0;
    border-radius: 10px;
    background: #fff;
    color: #0b4f8a;
    font-weight: 700;
    cursor: pointer;
  }
}

.product-card__select {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 650;
  cursor: pointer;

  input {
    accent-color: var(--accent);
  }
}

.labels-modal {
  overflow: hidden;
  border-radius: 18px;
  background: #f2f5f8;
}

.labels-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 14px;
  background: #fff;

  h2,
  p {
    margin: 0;
  }

  h2 {
    font-size: 20px;
  }

  p {
    margin-top: 5px;
    color: var(--muted);
    font-size: 13px;
  }
}

.labels-modal__close {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 0;
  border-radius: 9px;
  background: #eef3f7;
  color: var(--muted);
  cursor: pointer;
}

.labels-modal__settings {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  background: #fff;
  border-top: 1px solid #e5ebf1;

  .v-input {
    max-width: 360px;
  }

  span {
    color: var(--muted);
    font-size: 13px;
    font-weight: 650;
  }
}

.labels-printable {
  max-height: 58vh;
  overflow: auto;
}

.labels-sheet {
  width: var(--sheet-width, 194mm);
  min-height: var(--sheet-height, 281mm);
  display: grid;
  grid-template-columns: repeat(var(--label-columns), var(--label-width));
  grid-auto-rows: var(--label-height);
  gap: var(--label-gap, 0);
  justify-content: space-between;
  align-content: start;
  margin: 18px;
  background: #fff;

  &--roll {
    margin: 12px auto;
    justify-content: start;
    border: 1px solid #e5edf5;
    padding: 1mm;
  }
}

.product-label {
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 2px;
  padding: 3mm;
  border: 1px dashed #9fb0c1;
  border-radius: 2mm;
  text-align: center;

  &--mini {
    gap: 1px;
    padding: 1.2mm;

    .product-label__name { font-size: 8px; }
    .product-label__prices b { font-size: 11px; }
    .product-label__prices s { font-size: 7px; }
    .product-label__barcode-wrap { padding: .6mm; }
    .product-label__barcode { height: 16px; }
    .product-label__barcode-wrap span { font-size: 6px; }
  }

  &--compact {
    padding: 2mm;

    .product-label__name { font-size: 11px; }
    .product-label__prices b { font-size: 16px; }
    .product-label__barcode { height: 24px; }
    .product-label__barcode-wrap span { font-size: 8px; }
  }

  &--roll25x15,
  &--roll25x20,
  &--roll30x20,
  &--roll30x25 {
    gap: 0;
    padding: 0.7mm;
    border-radius: 1.2mm;

    .product-label__name {
      font-size: 7px;
      -webkit-line-clamp: 1;
    }

    .product-label__prices b { font-size: 11px; }
    .product-label__prices s { font-size: 6px; }
    .product-label__barcode-wrap { padding: 0.3mm; }
    .product-label__barcode-wrap span { font-size: 5px; }
  }

  &--roll25x15 .product-label__barcode { height: 10px; }
  &--roll25x20 .product-label__barcode,
  &--roll30x20 .product-label__barcode { height: 14px; }
  &--roll30x25 .product-label__barcode { height: 18px; }
}

.product-label__name {
  display: -webkit-box;
  overflow: hidden;
  max-width: 100%;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.15;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.product-label__prices {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;

  b {
    color: #b42318;
    font-size: 22px;
  }

  s {
    color: var(--muted);
    font-size: 12px;
  }
}

.product-label__barcode-wrap {
  display: grid;
  width: 100%;
  justify-items: center;
  gap: 1px;
  padding: 1.5mm 1mm 1mm;
  border-radius: 1.5mm;
  background: #f5f7fa;

  span {
    color: #506176;
    font-size: 9px;
    letter-spacing: .08em;
  }
}

.product-label__barcode {
  max-width: 100%;
  height: 42px;
}

.labels-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 18px 18px;
}

.labels-modal__btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 11px 14px;
  border: 0;
  border-radius: 10px;
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: .65;
  }

  &--ghost {
    background: #e4ebf2;
    color: #3d4f63;
  }
}

@media (max-width: 720px) {
  .products-page {
    padding: 20px 16px 32px;
  }

  .products-page__header {
    flex-direction: column;
  }

  .product-card__meta {
    grid-template-columns: 1fr;
  }

  .product-form-dialog__header {
    padding: 18px 16px 14px;
  }

  .product-form-dialog__body {
    max-height: 67vh;
    padding: 16px 16px 4px !important;
  }

  .product-form-dialog__actions {
    padding: 12px 16px 16px !important;
  }

  .product-form-grid,
  .product-form-grid--three {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .product-form-section__head {
    align-items: flex-start;
    flex-direction: column;
  }

  .product-form-row,
  .product-form-row--waste {
    grid-template-columns: 1fr 40px;
  }

  .product-form-row > *:not(.product-form-row__remove) {
    grid-column: 1;
  }

  .product-form-row__remove {
    grid-column: 2;
    grid-row: 1;
  }

  .product-photo-field {
    align-items: flex-start;
  }

  .labels-modal__settings {
    align-items: stretch;
    flex-direction: column;

    .v-input {
      max-width: none;
    }
  }

  .products-page__labels-bar {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
